import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Stack,
  Panel,
  PanelSection,
  FormLayout,
  FormLayoutGroup,
  Button,
  Banner,
  Link,
  Paragraph,
  useSnackbars,
  Box,
  Text,
  Spinner,
  Tag,
  Heading,
  Tooltip,
  Icon,
} from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { useStore } from '@nanostores/react';
import { workspaceStore } from '@/services/auth/store';
import { appendMasqParam } from '@/utils/masq';
import { Form, useForm, TextField, TextArea } from '@/components/form';
import { isEmptyString } from '@/utils/string';
import { accountVerificationApi } from './api';
import { z } from 'zod';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { CUSTOM_INDUSTRY_OPTION_VALUE } from '@/features/business-profile-forms';
import { servicesQuery } from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';
import type { AIContextOverrides } from '@/pages/journeys/settings/ai-business-profile/types';
import { BasicInfoFields, KeyLinksFields } from '@/features/business-profile-forms';
import { isAPIError } from '@/services/api/ts-rest';
import { useVerifiedDomains, useAIProfileSelection } from './hooks';

const MAX_TEXT_FIELD_LENGTH = 300;
const MIN_VALID_DATE = new Date('2020-01-01');

const cleanDomain = (domain: string): string => {
  return domain.replace(/^https?:\/\//, '');
};

const formatDateForTooltip = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  });
};

const getFormDefaultValues = (
  aiFormData:
    | {
        name?: string;
        websiteUrl?: string;
        industry?: string;
        customIndustry?: string;
        longDescription?: string;
        privacyPolicyUrl?: string;
        termsOfServiceUrl?: string;
      }
    | null
    | undefined,
  accountName?: string | null,
  userEmail?: string | null,
) => ({
  name: aiFormData?.name ?? accountName ?? '',
  websiteUrl: aiFormData?.websiteUrl ?? '',
  industry: aiFormData?.industry ?? '',
  customIndustry: aiFormData?.customIndustry ?? '',
  longDescription: aiFormData?.longDescription ?? '',
  privacyPolicyUrl: aiFormData?.privacyPolicyUrl ?? '',
  termsOfServiceUrl: aiFormData?.termsOfServiceUrl ?? '',
  replyTo: userEmail ?? '',
});

const trimIfNotEmpty = (value: string | undefined): string | undefined => {
  if (value == null) return undefined;
  const trimmed = value.trim();
  return !isEmptyString(trimmed) ? trimmed : undefined;
};

export function AccountVerificationContent() {
  const auth = useAuth();
  const workspaces = useStore(workspaceStore);
  const currentWorkspace = useCurrentWorkspace();
  const masqLink = (url: string) => appendMasqParam(url);
  const snackbars = useSnackbars();
  const queryClient = useQueryClient();

  const { domains: verifiedDomains, isLoading: isLoadingDomains } = useVerifiedDomains(workspaces);
  const {
    selectedProfile,
    aiContext,
    aiFormData,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useAIProfileSelection(workspaces, currentWorkspace?.id);

  const profileWorkspaceId = selectedProfile?.workspaceId ?? null;
  const fallbackWorkspaceId =
    currentWorkspace?.id ??
    (workspaces != null && workspaces.length > 0 ? Number.parseInt(workspaces[0].id, 10) : null);
  const workspaceIdForUpdate = profileWorkspaceId ?? fallbackWorkspaceId;

  const updateAIContextMutation = servicesQuery.updateAIContextOverrides.useMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const previousAiFormDataRef = useRef(aiFormData);
  const formRef = useRef<{ reset: (values: ReturnType<typeof getFormDefaultValues>) => void } | null>(null);

  const isReadOnly = auth.user.account_permissions?.includes('account/edit') !== true;

  const updatedAt = aiContext?.updated_at;
  const hasValidUpdatedAt = updatedAt != null && typeof updatedAt === 'string' && !isEmptyString(updatedAt);
  const hasValidDomain = typeof aiContext?.domain === 'string' && !isEmptyString(aiContext.domain);
  const showGenerationInfo =
    aiContext != null && hasValidUpdatedAt && hasValidDomain && new Date(updatedAt) > MIN_VALID_DATE;

  const validators = useMemo(
    () => ({
      onChange: z.object({
        name: z
          .string()
          .max(MAX_TEXT_FIELD_LENGTH, `Business name must be at most ${MAX_TEXT_FIELD_LENGTH} characters`),
        websiteUrl: z
          .string()
          .min(1, "Website can't be blank")
          .refine(
            (value) => {
              try {
                const url = new URL(value);
                return ['http:', 'https:'].includes(url.protocol);
              } catch {
                return false;
              }
            },
            { message: 'Please enter a valid URL starting with https:// or http://' },
          ),
        industry: z.string().max(MAX_TEXT_FIELD_LENGTH, `Industry must be at most ${MAX_TEXT_FIELD_LENGTH} characters`),
        customIndustry: z
          .string()
          .max(MAX_TEXT_FIELD_LENGTH, `Industry must be at most ${MAX_TEXT_FIELD_LENGTH} characters`),
        longDescription: z
          .string()
          .max(
            MAX_TEXT_FIELD_LENGTH * 10,
            `Business description must be at most ${MAX_TEXT_FIELD_LENGTH * 10} characters`,
          ),
        privacyPolicyUrl: z.union([
          z.literal(''),
          z.string().url('Please enter a valid URL (e.g., https://example.com/privacy)'),
        ]),
        termsOfServiceUrl: z.union([
          z.literal(''),
          z.string().url('Please enter a valid URL (e.g., https://example.com/terms-of-service)'),
        ]),
        replyTo: z.string().min(1, 'Reply-to email is required').email('Please enter a valid email address'),
      }),
    }),
    [],
  );

  const handleSubmit = useCallback(
    async ({ value }: { value: ReturnType<typeof getFormDefaultValues> }) => {
      setIsSubmitting(true);
      try {
        await accountVerificationApi.verifyAccount({
          accountId: String(auth.account.id),
          email: value.replyTo.trim(),
          website: value.websiteUrl.trim(),
          privacyPolicyUrl: trimIfNotEmpty(value.privacyPolicyUrl),
          termsOfServiceUrl: trimIfNotEmpty(value.termsOfServiceUrl),
        });

        const hasValidWorkspace = workspaceIdForUpdate != null && workspaceIdForUpdate > 0;
        let aiProfileUpdated = false;
        if (hasValidWorkspace) {
          try {
            const overrides: AIContextOverrides = {
              name: trimIfNotEmpty(value.name),
              website_url: trimIfNotEmpty(value.websiteUrl),
              industry:
                value.industry === CUSTOM_INDUSTRY_OPTION_VALUE
                  ? trimIfNotEmpty(value.customIndustry)
                  : trimIfNotEmpty(value.industry),
              long_description: trimIfNotEmpty(value.longDescription),
              privacy_policy_url: trimIfNotEmpty(value.privacyPolicyUrl),
              terms_of_service_url: trimIfNotEmpty(value.termsOfServiceUrl),
            };

            const filteredOverrides = Object.fromEntries(
              Object.entries(overrides).filter(([_, v]) => v != null && v !== ''),
            ) as AIContextOverrides;

            if (Object.keys(filteredOverrides).length > 0) {
              await updateAIContextMutation.mutateAsync({
                params: { workspaceId: workspaceIdForUpdate },
                body: { overrides: filteredOverrides },
              });
              queryClient.invalidateQueries({ queryKey: ['aiContextOverrides', workspaceIdForUpdate] });
              aiProfileUpdated = true;
            }
          } catch {
            // Silently ignore AI profile update failures
          }
        }

        const successMessage =
          hasValidWorkspace && aiProfileUpdated
            ? `Account verification request submitted! Your AI business profile has been updated. Our team will review within 24-48 hours.`
            : hasValidWorkspace
            ? `Account verification request submitted! Our team will review within 24-48 hours. Note: AI profile update failed.`
            : 'Account verification request submitted! Our team will review within 24-48 hours.';

        snackbars.create({
          variant: 'success',
          id: 'account.verification.success',
          message: successMessage,
          action:
            hasValidWorkspace && workspaceIdForUpdate != null && aiProfileUpdated
              ? {
                  label: 'View AI business profile',
                  onClick: () => {
                    window.location.href = masqLink(`/workspaces/${workspaceIdForUpdate}/settings/ai-business-profile`);
                  },
                }
              : undefined,
        });

        formRef.current?.reset(value);
      } catch (error: unknown) {
        let errorMessage = 'An error occurred. Please contact win@customer.io for help verifying your account.';

        if (isAPIError(error)) {
          if (typeof error.message === 'string' && !isEmptyString(error.message)) {
            errorMessage = error.message;
          }
        }

        snackbars.create({
          variant: 'error',
          id: 'account.verification.error',
          message: errorMessage,
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [auth.account?.id, workspaceIdForUpdate, updateAIContextMutation, queryClient, masqLink, snackbars],
  );

  const form = useForm({
    defaultValues: getFormDefaultValues(aiFormData, auth.account?.name, auth.user?.email),
    validators,
    onSubmit: handleSubmit,
  });

  formRef.current = form;

  useEffect(() => {
    if (aiFormData != null && aiFormData !== previousAiFormDataRef.current) {
      form.reset(getFormDefaultValues(aiFormData, auth.account?.name, auth.user?.email));
      previousAiFormDataRef.current = aiFormData;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form ref changes every render, only trigger on aiFormData change
  }, [aiFormData, auth.account?.name, auth.user?.email]);

  if (isLoadingProfile) {
    return (
      <Box display="flex" justifyContent="center" p="400">
        <Spinner />
      </Box>
    );
  }

  if (profileError != null) {
    return (
      <Box p="400">
        <Banner variant="error">Failed to load AI profile. Please refresh the page.</Banner>
      </Box>
    );
  }

  return (
    <Box p="400">
      <Stack gap="400">
        <Panel>
          <PanelSection>
            <Form
              api={form}
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit();
              }}
            >
              <FormLayout>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb="300">
                  <Heading level="2">Verify your account to send emails</Heading>
                  {showGenerationInfo ? (
                    <Tooltip
                      content={`The information below was automatically generated by analyzing your sending domain ${cleanDomain(
                        aiContext.domain,
                      )} on ${formatDateForTooltip(
                        aiContext.updated_at,
                      )}. Our AI crawlers and enrichment pipeline gathered this data from your website and public sources. You can override any details that seem inaccurate — AI can sometimes hallucinate.`}
                      placement="bottom"
                      isCustom={true}
                    >
                      {(context) => (
                        <Box
                          ref={context.reference.setReference}
                          {...context.reference.getReferenceProps()}
                          display="inline-flex"
                          alignItems="center"
                          style={{ cursor: 'pointer' }}
                        >
                          <Icon name="info" color="subtle" />
                          <Text ml="100" color="subtle">
                            Where did this information come from?
                          </Text>
                        </Box>
                      )}
                    </Tooltip>
                  ) : null}
                </Box>
                <BasicInfoFields form={form} isDisabled={isReadOnly} autoFocus={true} />
                <TextArea
                  name="longDescription"
                  api={form}
                  label="Business description"
                  placeholder="Briefly describe what your business does and the value you provide"
                  minRows={4}
                  description="Explain what your business does, the products or services you offer, and the value you provide to customers"
                  isDisabled={isReadOnly}
                />
                <KeyLinksFields
                  form={form}
                  isDisabled={isReadOnly}
                  fields={['privacyPolicyUrl', 'termsOfServiceUrl']}
                />

                {workspaces != null && workspaces.length > 0 ? (
                  <Box display="inline-flex" flexDirection="column" style={{ width: 'fit-content' }}>
                    <Text as="label" weight="medium" mb="100">
                      Domains
                    </Text>
                    {isLoadingDomains ? (
                      <Box display="flex" alignItems="center" gap="100" mt="100">
                        <Spinner />
                        <Text>Loading domains...</Text>
                      </Box>
                    ) : verifiedDomains.length > 0 ? (
                      <Stack gap="100" mt="100">
                        {verifiedDomains.map((domain) => (
                          <Tag key={domain}>{domain}</Tag>
                        ))}
                      </Stack>
                    ) : (
                      <Text mt="100" color="subtle">
                        No verified domains found.
                      </Text>
                    )}
                  </Box>
                ) : null}

                <Heading level="2" mb="200" mt="200">
                  Contact information
                </Heading>

                <TextField
                  name="replyTo"
                  api={form}
                  label="Reply-to email"
                  description="Whom should we contact if we need additional details?"
                  placeholder={auth.user?.email ?? 'your@email.com'}
                  type="email"
                  isDisabled={isReadOnly}
                />

                <Banner variant="information" mt="200" mb="200">
                  <Paragraph>
                    By submitting this request, you are granting consent for our staff to access your account and data
                    in order to verify compliance with our{' '}
                    <Link href="https://customer.io/legal/terms-of-service" variant="secondary" isExternal>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link href="https://customer.io/legal/acceptable-use-policy" variant="secondary" isExternal>
                      Acceptable Use Policy
                    </Link>
                    .
                    {workspaceIdForUpdate != null && workspaceIdForUpdate > 0 ? (
                      <>
                        {' '}
                        The information provided will also be saved to your{' '}
                        <Link
                          href={masqLink(`/workspaces/${workspaceIdForUpdate}/settings/ai-business-profile`)}
                          variant="secondary"
                        >
                          AI business profile
                        </Link>
                        .
                      </>
                    ) : null}
                  </Paragraph>
                </Banner>

                <FormLayoutGroup>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="primary"
                      isDisabled={isSubmitting || isReadOnly || form.state.isValid !== true}
                    >
                      {isSubmitting ? 'Submitting…' : 'Verify my account'}
                    </Button>
                  </Box>
                </FormLayoutGroup>
              </FormLayout>
            </Form>
          </PanelSection>
        </Panel>
      </Stack>
    </Box>
  );
}
